/**
 * ProcessCard Component
 *
 * A reusable card component for displaying process steps in the Services section.
 * Features:
 * - Responsive width and layout for mobile and desktop
 * - Animated shadow and scale on hover for interactivity
 * - Accepts a title and description as props
 *
 * @param {ProcessCardProps} props - Props for the ProcessCard component
 * @param {string} props.title - The step title to display
 * @param {React.ReactNode} props.description - The step description (can be string or JSX)
 *
 * @returns {JSX.Element} The rendered process card
 */
'use client';

import React from 'react';

interface ProcessCardProps {
  title: string;
  description: React.ReactNode;
}

/**
 * Renders a styled card for a single process step.
 */
export default function ProcessCard({ title, description }: ProcessCardProps) {
  return (
    <div className="w-full md:w-[433px] bg-white border border-black/10 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group hover:scale-[1.02] flex flex-col h-full">
      <div className="flex flex-col gap-4 flex-grow">
        {/* Step Title */}
        <h3 className="font-manrope font-semibold text-androia-text text-2xl tracking-[-0.48px] leading-[40px]">
          {title}
        </h3>
        {/* Step Description */}
        <p className="font-manrope font-medium text-androia-text text-lg tracking-[-0.36px] leading-[32px] flex-grow">
          {description}
        </p>
      </div>
    </div>
  );
}
